import axios from 'axios';
import { 
    ContentSource, 
    Section, 
    CreateContentSourceForm, 
    UpdateContentSourceForm, 
    CreateSectionForm, 
    UpdateSectionForm, 
    ReviewSectionForm, 
    AssignContentSourceForm, 
    ReorderSectionsForm,
    ContentSourcesResponse,
    AutoSaveResponse,
    ExportData,
    FilterState
} from '../types/cms';

// Create axios instance with base configuration
const cmsApi = axios.create({
    baseURL: '/api/cms',
    timeout: 30000, // 30 seconds timeout for large operations
});

// Request interceptor to add auth token
cmsApi.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
cmsApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Handle unauthorized access
            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Content Source API functions
export const contentSourceApi = {
    // Get all content sources with pagination and filtering
    getAll: async (filters: Partial<FilterState> = {}): Promise<ContentSourcesResponse> => {
        const params = new URLSearchParams();
        
        // Add pagination
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.limit) params.append('limit', filters.limit.toString());
        
        // Add sorting
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
        
        // Add filters
        if (filters.search) params.append('search', filters.search);
        if (filters.status) params.append('status', filters.status);
        if (filters.filter_tags?.length) params.append('filter_tags', filters.filter_tags.join(','));
        if (filters.assigned_to_id) params.append('assigned_to_id', filters.assigned_to_id);
        if (filters.created_by_id) params.append('created_by_id', filters.created_by_id);
        if (filters.updated_by_id) params.append('updated_by_id', filters.updated_by_id);
        if (filters.visibility) params.append('visibility', filters.visibility);
        if (filters.review_status) params.append('review_status', filters.review_status);

        const response = await cmsApi.get(`/content-sources?${params.toString()}`);
        return response.data;
    },

    // Get content sources assigned to subordinate users (admins only)
    getSubordinateAssigned: async (filters: Partial<FilterState> = {}): Promise<{ data: ContentSource[]; filters: any }> => {
        const params = new URLSearchParams();
        
        // Add sorting
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
        
        // Add filters
        if (filters.search) params.append('search', filters.search);
        if (filters.status) params.append('status', filters.status);
        if (filters.filter_tags?.length) params.append('filter_tags', filters.filter_tags.join(','));
        if (filters.assigned_to_id) params.append('assigned_to_id', filters.assigned_to_id);
        if (filters.created_by_id) params.append('created_by_id', filters.created_by_id);
        if (filters.updated_by_id) params.append('updated_by_id', filters.updated_by_id);
        if (filters.visibility) params.append('visibility', filters.visibility);
        if (filters.review_status) params.append('review_status', filters.review_status);

        const response = await cmsApi.get(`/content-sources/subordinates/assigned?${params.toString()}`);
        return response.data;
    },

    // Get content source by ID
    getById: async (id: string): Promise<ContentSource> => {
        const response = await cmsApi.get(`/content-sources/${id}`);
        return response.data;
    },

    // Create new content source
    create: async (data: CreateContentSourceForm): Promise<ContentSource> => {
        const response = await cmsApi.post('/content-sources', data);
        return response.data;
    },

    // Update content source
    update: async (id: string, data: UpdateContentSourceForm): Promise<ContentSource> => {
        const response = await cmsApi.patch(`/content-sources/${id}`, data);
        return response.data;
    },

    // Delete content source
    delete: async (id: string): Promise<void> => {
        await cmsApi.delete(`/content-sources/${id}`);
    },

    // Assign content source to user
    assign: async (id: string, data: AssignContentSourceForm): Promise<{ message: string; contentSource: ContentSource }> => {
        const response = await cmsApi.post(`/content-sources/${id}/assign`, data);
        return response.data;
    },

    // Export content source for AI
    export: async (id: string): Promise<ExportData> => {
        const response = await cmsApi.get(`/content-sources/${id}/export`);
        return response.data;
    },

    // Bulk assign content sources for review
    bulkAssignForReview: async (contentSourceIds: string[], userId: string): Promise<{ message: string; count: number }> => {
        const response = await cmsApi.post('/content-sources/bulk-assign-review', {
            contentSourceIds,
            userId
        });
        return response.data;
    }
};

// Section API functions
export const sectionApi = {
    // Get sections by content source ID
    getByContentSource: async (
        contentSourceId: string, 
        options: {
            depth?: number;
            parent_id?: string;
            ref_visibility?: string;
            timeout?: number;
        } = {}
    ): Promise<Section[]> => {
        const params = new URLSearchParams();
        
        if (options.depth) params.append('depth', options.depth.toString());
        if (options.parent_id) params.append('parent_id', options.parent_id);
        if (options.ref_visibility) params.append('ref_visibility', options.ref_visibility);

        // Use custom timeout if provided, otherwise use default (120s for large section loads)
        const timeout = options.timeout ?? 120000; // 120 seconds default for large section loads

        const response = await cmsApi.get(`/content-sources/${contentSourceId}/sections?${params.toString()}`, {
            timeout
        });
        return response.data;
    },

    // Get section by ID
    getById: async (id: string): Promise<Section> => {
        const response = await cmsApi.get(`/sections/${id}`);
        return response.data;
    },

    // Create new section
    create: async (contentSourceId: string, data: CreateSectionForm): Promise<Section> => {
        const response = await cmsApi.post(`/content-sources/${contentSourceId}/sections`, data);
        return response.data;
    },

    // Update section
    update: async (id: string, data: UpdateSectionForm): Promise<Section | AutoSaveResponse> => {
        const response = await cmsApi.patch(`/sections/${id}`, data);
        return response.data;
    },

    // Review section (copy-on-edit)
    review: async (id: string, data: ReviewSectionForm): Promise<Section> => {
        const response = await cmsApi.post(`/sections/${id}/review`, data);
        return response.data;
    },

    // Delete section
    delete: async (id: string): Promise<void> => {
        await cmsApi.delete(`/sections/${id}`);
    },

    // Reorder sections
    reorder: async (contentSourceId: string, data: ReorderSectionsForm): Promise<{ message: string }> => {
        const response = await cmsApi.post(`/content-sources/${contentSourceId}/sections/reorder`, data);
        return response.data;
    }
};

// Utility functions
export const cmsUtils = {
    // Generate TipTap HTML from JSON
    generateHtml: (json: Record<string, any> | null): string => {
        if (!json) return '';
        
        // This is a simplified HTML generator
        // In a real implementation, you'd use TipTap's generateHTML or similar
        try {
            if (json.type === 'doc' && json.content) {
                return json.content.map((node: any) => {
                    switch (node.type) {
                        case 'paragraph':
                            return `<p>${node.content?.map((c: any) => c.text || '').join('') || ''}</p>`;
                        case 'heading':
                            const level = node.attrs?.level || 1;
                            return `<h${level}>${node.content?.map((c: any) => c.text || '').join('') || ''}</h${level}>`;
                        case 'bulletList':
                            return `<ul>${node.content?.map((c: any) => `<li>${c.content?.map((cc: any) => cc.text || '').join('') || ''}</li>`).join('') || ''}</ul>`;
                        case 'orderedList':
                            return `<ol>${node.content?.map((c: any) => `<li>${c.content?.map((cc: any) => cc.text || '').join('') || ''}</li>`).join('') || ''}</ol>`;
                        default:
                            return node.text || '';
                    }
                }).join('');
            }
            return '';
        } catch (error) {
            console.error('Error generating HTML:', error);
            return '';
        }
    },

    // Generate breadcrumb path
    generateBreadcrumb: (path: string): string[] => {
        return path.split('.').map((segment, index) => {
            const depth = index + 1;
            return `${depth}.${segment}`;
        });
    },

    // Check if user can perform action
    canPerformAction: (userRole: string, action: string, resource?: any): boolean => {
        switch (action) {
            case 'create_content_source':
                return ['admin', 'superadmin', 'editor', 'editor-reviewer'].includes(userRole);
            
            case 'edit_content_source':
                if (!resource) return false;
                return ['admin', 'superadmin'].includes(userRole) || 
                       (['editor', 'editor-reviewer'].includes(userRole) && 
                        (resource.assigned_to_id === resource.created_by_id || true));
            
            case 'review_content_source':
                if (!resource) return false;
                return ['reviewer', 'editor-reviewer', 'admin', 'superadmin'].includes(userRole) &&
                       (resource.status === 'ready_for_review' || resource.status === 'ethics_review' || resource.status === 'ethics_review_in_process');
            
            case 'delete_content_source':
                return ['admin', 'superadmin'].includes(userRole);
            
            case 'assign_content_source':
                return ['admin', 'superadmin'].includes(userRole);
            
            case 'create_section':
                if (!resource) return false;
                return ['admin', 'superadmin'].includes(userRole) || 
                       (['editor', 'editor-reviewer'].includes(userRole) && 
                        (resource.assigned_to_id === resource.created_by_id || true));
            
            case 'edit_section':
                if (!resource) return false;
                return ['admin', 'superadmin'].includes(userRole) || 
                       (['editor', 'editor-reviewer'].includes(userRole) && 
                        (resource.content_source?.assigned_to_id === resource.content_source?.created_by_id || 
                         true));
            
            case 'review_section':
                if (!resource) return false;
                return ['reviewer', 'editor-reviewer', 'admin', 'superadmin'].includes(userRole) &&
                       (resource.content_source?.status === 'ready_for_review' || resource.content_source?.status === 'ethics_review' || resource.content_source?.status === 'ethics_review_in_process');
            
            case 'delete_section':
                if (!resource) return false;
                return ['admin', 'superadmin'].includes(userRole) || 
                       (['editor', 'editor-reviewer'].includes(userRole) && 
                        (resource.content_source?.assigned_to_id === resource.content_source?.created_by_id || 
                         true));
            
            default:
                return false;
        }
    },

    // Format date for display
    formatDate: (date: Date | string): string => {
        const d = new Date(date);
        return d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    },

    // Get status color
    getStatusColor: (status: string): string => {
        const colors: Record<string, string> = {
            draft: 'gray',
            ready_for_review: 'yellow',
            ethics_review: 'purple',
            ethics_review_in_process: 'orange',
            ethics_reviewed: 'green',
            entity_extraction_in_process: 'teal',
            entity_reviewed: 'blue',
            published: 'red',
            none: 'gray',
            escalate: 'red',
            resolved: 'green',
            public: 'green',
            karyakar: 'blue',
            sant: 'purple',
            hidden: 'red'
        };
        return colors[status] || 'gray';
    },

    // Debounce function
    debounce: <T extends (...args: any[]) => any>(
        func: T,
        wait: number
    ): ((...args: Parameters<T>) => void) => {
        let timeout: NodeJS.Timeout;
        return (...args: Parameters<T>) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), wait);
        };
    },

    // Throttle function
    throttle: <T extends (...args: any[]) => any>(
        func: T,
        limit: number
    ): ((...args: Parameters<T>) => void) => {
        let inThrottle: boolean;
        return (...args: Parameters<T>) => {
            if (!inThrottle) {
                func(...args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }
};

// Review API functions
export const reviewApi = {
    // Get all content sources assigned to current user with status 'ethics_review_in_process'
    getAssigned: async (): Promise<ContentSource[]> => {
        const response = await cmsApi.get('/review/assigned');
        return response.data;
    },

    // Fuzzy search through assigned content sources and sections
    search: async (query: string): Promise<Array<{
        section: Section;
        contentSource: ContentSource;
        breadcrumb: string[];
        matchText: string;
    }>> => {
        const response = await cmsApi.get(`/review/search?q=${encodeURIComponent(query)}`);
        return response.data;
    },

    // Mark all assigned content sources as reviewed
    completeReview: async (): Promise<{ message: string; count: number }> => {
        const response = await cmsApi.post('/review/complete');
        return response.data;
    }
};

// Export default API object
const cmsApiService = {
    contentSource: contentSourceApi,
    section: sectionApi,
    review: reviewApi,
    utils: cmsUtils
};

export default cmsApiService;
